import { useEffect, useState } from "react";
import { merchantFetch } from "../lib/merchantAuth";

/**
 * Merchant-side identity editor — surfaced inside the Merchant
 * Settings page so an OWNER can fix their own business name, owner
 * email (recovery anchor), owner phone (2FA target), and ops contact
 * phone without going through admin support.
 *
 * Reads `GET /api/v1/tenant/me/profile` (any authenticated tenant
 * user role) and writes `PATCH /api/v1/tenant/me/profile` (OWNER
 * only — the server enforces RBAC and returns 403 otherwise).
 *
 * For non-OWNER viewers we render the section as read-only with a
 * note explaining who can edit. We detect "not-owner" by attempting
 * the GET; if a subsequent PATCH returns 403/FORBIDDEN we surface
 * that inline rather than crashing the page.
 */

interface ProfileResp {
  id: string;
  name: string;
  ownerEmail: string | null;
  ownerPhone: string | null;
  contactPhone: string | null;
}

const PH_PHONE = /^\+639\d{9}$/u;

export function MerchantProfileSection() {
  const [profile, setProfile] = useState<ProfileResp | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await merchantFetch<ProfileResp>("/api/v1/tenant/me/profile");
      if (cancelled) return;
      if (!r.ok) {
        setLoadErr(r.message);
        return;
      }
      setProfile(r.data);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  function startEdit() {
    if (!profile) return;
    // `name` is intentionally NOT writable here — the server's
    // `TenantProfileUpdateRequestSchema` rejects a `name` field
    // (renaming the tenant has cross-cutting effects on the
    // customer-facing brand directory and is admin-only). We seed
    // it for display only.
    setName(profile.name);
    setOwnerEmail(profile.ownerEmail ?? "");
    setOwnerPhone(profile.ownerPhone ?? "");
    setContactPhone(profile.contactPhone ?? "");
    setErr(null);
    setSavedAt(null);
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setErr(null);
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!profile) return;
    setErr(null);

    const body: Record<string, unknown> = {};
    // `name` is admin-only on the server side — see startEdit().
    // We omit it from the PATCH body even if the user typed in the
    // (read-only) field below.
    const emailDraft = ownerEmail.trim().toLowerCase();
    const emailInitial = (profile.ownerEmail ?? "").toLowerCase();
    if (emailDraft !== emailInitial) {
      if (emailDraft === "") {
        body.ownerEmail = null;
      } else if (!/.+@.+\..+/u.test(emailDraft)) {
        setErr("Owner email must be a valid email address.");
        return;
      } else {
        body.ownerEmail = emailDraft;
      }
    }
    const ownerPhoneDraft = ownerPhone.trim();
    if (ownerPhoneDraft !== (profile.ownerPhone ?? "")) {
      if (ownerPhoneDraft === "") {
        body.ownerPhone = null;
      } else if (!PH_PHONE.test(ownerPhoneDraft)) {
        setErr("Owner phone must be a PH mobile in +639XXXXXXXXX form.");
        return;
      } else {
        body.ownerPhone = ownerPhoneDraft;
      }
    }
    const contactPhoneDraft = contactPhone.trim();
    if (contactPhoneDraft !== (profile.contactPhone ?? "")) {
      if (contactPhoneDraft === "") {
        body.contactPhone = null;
      } else if (!PH_PHONE.test(contactPhoneDraft)) {
        setErr("Contact phone must be a PH mobile in +639XXXXXXXXX form.");
        return;
      } else {
        body.contactPhone = contactPhoneDraft;
      }
    }

    if (Object.keys(body).length === 0) {
      setErr("Nothing to save — change at least one field.");
      return;
    }

    setSaving(true);
    const r = await merchantFetch<ProfileResp>("/api/v1/tenant/me/profile", {
      method: "PATCH",
      body: JSON.stringify(body),
    });
    setSaving(false);
    if (!r.ok) {
      setErr(r.message);
      return;
    }
    setProfile(r.data);
    setEditing(false);
    setSavedAt(Date.now());
  }

  if (loadErr) {
    return (
      <section className="card mt-6">
        <p className="text-xs uppercase tracking-wider text-ink-dark">
          Business profile
        </p>
        <p className="mt-2 rounded-card bg-red-50 px-3 py-2 text-sm text-red-700">
          {loadErr}
        </p>
      </section>
    );
  }

  if (!profile) {
    return (
      <section className="card mt-6">
        <p className="text-xs uppercase tracking-wider text-ink-dark">
          Business profile
        </p>
        <p className="mt-2 text-sm text-ink-dark">Loading…</p>
      </section>
    );
  }

  return (
    <section className="card mt-6">
      <p className="text-xs uppercase tracking-wider text-ink-dark">
        Business profile
      </p>
      <p className="mt-1 text-sm text-ink-dark">
        Owner-only. Used for token recovery (email + phone) and for
        the merchant name shown in customer-facing messages.
      </p>

      {savedAt && (
        <p className="mt-2 text-xs text-green-700">Saved.</p>
      )}

      {!editing ? (
        <>
          <dl className="mt-4 space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-ink-dark">Business name</dt>
              <dd className="font-medium text-navy">{profile.name}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-ink-dark">Owner email</dt>
              <dd className="font-mono text-navy">
                {profile.ownerEmail ?? (
                  <span className="text-ink-dark italic">not set</span>
                )}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-ink-dark">Owner phone</dt>
              <dd className="font-mono text-navy">
                {profile.ownerPhone ?? (
                  <span className="text-ink-dark italic">not set</span>
                )}
              </dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-ink-dark">Contact phone</dt>
              <dd className="font-mono text-navy">
                {profile.contactPhone ?? (
                  <span className="text-ink-dark italic">not set</span>
                )}
              </dd>
            </div>
          </dl>
          <div className="mt-4">
            <button
              type="button"
              onClick={startEdit}
              className="btn-primary"
            >
              Edit profile
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={save} className="mt-4 space-y-3">
          <div className="block text-xs font-medium text-navy">
            Business name
            <p className="input mt-1 bg-amber-light/30 font-medium text-navy">
              {name}
            </p>
            <span className="mt-1 block text-[11px] font-normal text-ink-dark">
              Contact M42 support to rename your business.
            </span>
          </div>
          <label className="block text-xs font-medium text-navy">
            Owner email (recovery anchor)
            <input
              type="email"
              value={ownerEmail}
              onChange={(e) => setOwnerEmail(e.target.value)}
              placeholder="owner@example.com"
              className="input mt-1 font-mono"
              autoComplete="email"
            />
            <span className="mt-1 block text-[11px] font-normal text-ink-dark">
              Leave blank to clear. Lowercased on save.
            </span>
          </label>
          <label className="block text-xs font-medium text-navy">
            Owner phone (2FA target)
            <input
              type="tel"
              value={ownerPhone}
              onChange={(e) => setOwnerPhone(e.target.value)}
              placeholder="+639XXXXXXXXX"
              className="input mt-1 font-mono"
              autoComplete="tel"
            />
          </label>
          <label className="block text-xs font-medium text-navy">
            Contact phone (ops)
            <input
              type="tel"
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              placeholder="+639XXXXXXXXX"
              className="input mt-1 font-mono"
              autoComplete="tel"
            />
          </label>
          {err && (
            <p className="rounded-card bg-red-50 px-3 py-2 text-sm text-red-700">
              {err}
            </p>
          )}
          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              type="button"
              onClick={cancelEdit}
              className="btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
